import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const ACTION_LABEL = {
  INSERT: '作成',
  UPDATE: '変更',
  DELETE: '削除'
};
const ACTION_COLOR = {
  INSERT: '#22c55e',
  UPDATE: '#f59e0b',
  DELETE: '#ef4444'
};
const FIELD_LABEL = {
  student_id: '学籍番号',
  name:       '氏名',
  email:      'メール',
  role:       '権限',
  created:    '新規登録',
  deleted:    '削除'
};

export default function UsersAudit() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [studentId, setSid]   = useState('');
  const [action, setAction]   = useState('');
  const [fieldName, setField] = useState('');
  const [since, setSince]     = useState('');
  const [until, setUntil]     = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  // 変更前・変更後の値は教務・システム管理者だけ（サーバーが値を返さない）【2026-09-17】
  const [canSeeValues, setCanSeeValues] = useState(true);

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (studentId) params.append('studentId', studentId);
      if (action)    params.append('action', action);
      if (fieldName) params.append('fieldName', fieldName);
      if (since)     params.append('since', since);
      if (until)     params.append('until', until);
      params.append('limit', '200');

      const res  = await fetch('/api/admin/users-audit?' + params, { headers: auth() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '取得失敗');
      setLogs(data.logs || []);
      setCanSeeValues(data.canSeeValues !== false);
      setTotal(data.total || 0);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 開いたときに1回だけ（絞り込みは「検索」ボタンで読み直す）

  const reset = () => { setSid(''); setAction(''); setField(''); setSince(''); setUntil(''); };

  return (
    <div className="page-container">
      <div className="page-header">
        {/* サイドバーから外したので、来た道を示しておく【2026-09-17】 */}
        <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
          <Link to="/profile">← アカウント設定</Link>
          <span style={{ color: '#ccc', margin: '0 0.5rem' }}>|</span>
          <Link to="/students">学生管理</Link>
        </div>
        <h1>ユーザー変更履歴</h1>
        <p>学生情報（学籍番号・氏名・メール・権限）の変更履歴を時系列で表示します。</p>
      </div>

      <div className="card">
        <div className="card-header"><h2>絞り込み</h2></div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>学籍番号</label>
            <input value={studentId} onChange={e => setSid(e.target.value)} placeholder="部分一致"/>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>操作</label>
            <select value={action} onChange={e => setAction(e.target.value)}>
              <option value="">— すべて —</option>
              <option value="INSERT">作成</option>
              <option value="UPDATE">変更</option>
              <option value="DELETE">削除</option>
            </select>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>変更項目</label>
            <select value={fieldName} onChange={e => setField(e.target.value)}>
              <option value="">— すべて —</option>
              <option value="student_id">学籍番号</option>
              <option value="name">氏名</option>
              <option value="email">メール</option>
              <option value="role">権限</option>
            </select>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>開始日</label>
            <input type="date" value={since} onChange={e => setSince(e.target.value)}/>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>終了日</label>
            <input type="date" value={until} onChange={e => setUntil(e.target.value)}/>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn-primary" onClick={load} disabled={loading}>
            {loading ? '検索中...' : '検索'}
          </button>
          <button className="btn-secondary" onClick={reset}>クリア</button>
        </div>
      </div>

      {err && <div className="card" style={{borderLeft:'4px solid #ef4444'}}><p style={{color:'#c62828'}}>{err}</p></div>}

      <div className="card">
        <div className="card-header" style={{display:'flex',justifyContent:'space-between'}}>
          <h2>履歴一覧</h2>
          <span style={{color:'#666'}}>{logs.length} / 総 {total} 件</span>
        </div>

        {!canSeeValues && (
          <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 0.6rem' }}>
            変更前・変更後の値は、教務とシステム管理者だけが見られます。ここでは「いつ・誰が・どの項目を」変えたかを表示しています。
          </p>
        )}
        {logs.length === 0 ? (
          <div style={{textAlign:'center',padding:'2rem',color:'#888'}}>履歴がありません</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>対象ユーザー</th>
                  <th>操作</th>
                  <th>項目</th>
                  {canSeeValues && <th>変更前</th>}
                  {canSeeValues && <th>変更後</th>}
                  <th>操作者</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => {
                  const color = ACTION_COLOR[l.action] || '#6b7280';
                  return (
                    <tr key={l.id}>
                      <td style={{whiteSpace:'nowrap'}}>{formatTs(l.changed_at)}</td>
                      <td>
                        {l.current_student_id ? (
                          <>
                            <strong>{l.current_name || '-'}</strong>
                            <br/><small style={{color:'#888'}}>{l.current_student_id}</small>
                          </>
                        ) : (
                          <span style={{color:'#888'}}>(削除済み id={l.user_id})</span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          display:'inline-block', padding:'0.15rem 0.6rem',
                          borderRadius:'10px', fontSize:'0.78rem', fontWeight:700,
                          background: color + '22', color
                        }}>
                          {ACTION_LABEL[l.action] || l.action}
                        </span>
                      </td>
                      <td>{FIELD_LABEL[l.field_name] || l.field_name || '-'}</td>
                      {canSeeValues && (
                        <td style={{maxWidth:'14rem',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {l.action === 'UPDATE' ? (l.old_value || '-') : '—'}
                        </td>
                      )}
                      {canSeeValues && (
                        <td style={{maxWidth:'14rem',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {l.action === 'INSERT' || l.action === 'DELETE'
                            ? (l.action === 'INSERT' ? l.new_value : l.old_value)
                            : (l.new_value || '-')}
                        </td>
                      )}
                      <td>
                        {l.changed_by_name
                          ? <>{l.changed_by_name}<br/><small style={{color:'#888'}}>{l.changed_by_student_id}</small></>
                          : <span style={{color:'#888'}}>システム</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ background: '#f9fafb' }}>
        <div className="card-header"><h2>補足</h2></div>
        <ul style={{ paddingLeft: '1.5rem', lineHeight: 1.9, fontSize:'0.92rem', color:'#555' }}>
          <li>このログはDBトリガで自動記録されており、API経由では削除できません。</li>
          <li>JSON文字列で記録されている項目（作成/削除時）は、そのまま表示しています。</li>
          <li>古いログを残すため、定期的なバックアップを推奨します。</li>
        </ul>
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('ja-JP', {
      year:'numeric', month:'2-digit', day:'2-digit',
      hour:'2-digit', minute:'2-digit', second:'2-digit'
    });
  } catch { return '-'; }
}
